import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useParams } from "react-router-dom";
import { usePatient, useClinicDoctors, useFlags, usePatientFlags } from "@/lib/queries";
import { useCreatePatient, useUpdatePatient, useUploadPatientPhoto, useDuplicatePatientCheck } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { INDIAN_STATES, GENDERS, SALUTATIONS } from "@emr/shared/constants";
import { validatePhone, validatePincode, validateAadhaar } from "@emr/shared/india";
import { swatchBg } from "@emr/shared/flags";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { X } from "lucide-react";
import { PatientPhotoCard } from "./PatientPhotoCard";
import { DuplicatePatientDialog, type DuplicatePatientMatch } from "./DuplicatePatientDialog";

// valueAsNumber: true on empty/disabled number inputs produces NaN.
// Zod's optional() only skips undefined, not NaN, so we coerce NaN/""/null → undefined here.
const nanToUndefined = (v: unknown) =>
  (typeof v === "number" && Number.isNaN(v)) || v === "" || v === null ? undefined : v;

// React 19 + Radix Select inside a <form>: when a controlled Select's value
// prop transitions (e.g. react-hook-form's reset() hydrating the saved
// salutation/gender/state), the hidden native "bubble" <select> that Radix
// renders for form association can end up with value "" and dispatch a native
// change event that reaches our onValueChange with "" — clobbering the just-
// hydrated form state and setting phantom "required" errors
// (radix-ui/primitives#3381, #4102; react#30580; manifests only on React 19).
// No legitimate user action can produce "" here: Radix ≥2 forbids empty-value
// SelectItems and none of our option lists contain one. So any "" arriving
// through onValueChange is this spurious programmatic event — ignore it.
const ignoreSpuriousEmpty = (apply: (v: string) => void) => (v: string) => {
  if (v === "") return;
  apply(v);
};

const schema = z.object({
  salutation: z.string().min(1, "Salutation is required"),
  name: z.string().min(1, "Name is required"),
  dob: z.string().optional(),
  noDob: z.boolean().default(false),
  ageYears: z.preprocess(nanToUndefined, z.number().int().min(0).max(130).optional()),
  ageMonths: z.preprocess(nanToUndefined, z.number().int().min(0).max(11).optional()),
  gender: z.enum(["Male", "Female", "Other"], { error: "Gender is required" }),
  phone: z.string().min(1, "Phone number is required").refine((v) => validatePhone(v), "Invalid phone (10-digit mobile or 0-STD landline)"),
  altPhone: z.string().optional().refine((v) => !v || validatePhone(v), "Invalid phone (10-digit mobile or 0-STD landline)"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  address: z.string().min(1, "Address is required"),
  state: z.string().min(1, "State is required"),
  pincode: z.string().optional().refine((v) => !v || validatePincode(v), "6-digit pincode"),
  aadhaarId: z.string().optional().refine((v) => !v || validateAadhaar(v), "Enter a valid 12-digit Aadhaar or 16-digit Virtual ID"),
  abhaId: z.string().optional(),
}).refine(
  // Either a literal DOB or an age (years) must be provided.
  (v) => v.noDob ? v.ageYears !== undefined : (v.dob !== undefined && v.dob !== ""),
  { message: "Enter date of birth or tick \"No date of birth available\" and enter age", path: ["dob"] },
).refine(
  (v) => !v.noDob || v.ageYears !== undefined,
  { message: "Enter age (years) when DOB is unavailable", path: ["ageYears"] },
).refine(
  (v) => v.noDob || !v.dob || (v.ageYears === undefined && v.ageMonths === undefined),
  { message: "Provide either DOB or age, not both", path: ["dob"] },
);

type FormValues = z.infer<typeof schema>;
// zod 4 + resolvers 5: the resolver types against the schema INPUT (where
// .default(false) makes noDob optional and z.preprocess makes age unknown),
// while submit receives the OUTPUT. RHF's 3-generic useForm expresses exactly
// this input→output mapping (documented @hookform/resolvers v5 pattern).
type FormInput = z.input<typeof schema>;

export function PatientForm({ patientId, onSaved }: { patientId?: string; onSaved?: (id: string) => void }) {
  const navigate = useNavigate();
  const { data, isLoading } = usePatient(patientId);
  const { data: flagsData } = useFlags();
  const { data: patientFlagsData } = usePatientFlags(patientId);
  const createMut = useCreatePatient();
  const updateMut = useUpdatePatient(patientId ?? "");
  // Duplicate-patient pre-check (2026-10-28): advisory check before create.
  const dupCheckMut = useDuplicatePatientCheck();
  // Uploads the staged photo right after a NEW patient is created.
  const photoUploadMut = useUploadPatientPhoto();
  const photoR2Key = patientId ? data?.patient?.photoR2Key : undefined;

  // Flags catalog is already sorted by sortOrder, name (server-side).
  const catalog = Array.isArray(flagsData) ? flagsData : (flagsData as any)?.rows ?? [];
  const initialFlagIds = (patientFlagsData?.rows ?? []).map((pf) => pf.flagId);

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<FormInput, any, FormValues>({
    resolver: zodResolver(schema),
    // New-patient defaults. Radix Select's `defaultValue` prop is purely visual —
    // it does NOT register with react-hook-form, so a state default of "TN" set
    // only on the <Select> would leave the form state as undefined and Zod would
    // reject it as "State is required" even though "Tamil Nadu" is displayed.
    // Registering the default here keeps form state in sync with the visual default.
    defaultValues: { state: "TN" } as Partial<FormValues>,
  });

  // In edit mode, hydrate the form with the patient's saved values once the data
  // arrives. We use an explicit `reset()` call rather than the `values` option
  // because `values` relies on deepEqual to detect changes — it can miss updates
  // when the component re-renders during async data loading (the new object literal
  // is a different reference but deepEqual sees the same shape and skips the reset,
  // leaving the Radix Selects showing "Select" instead of the saved value). `reset()`
  // unconditionally writes the form state and triggers a re-render so the controlled
  // Selects pick up the new values.
  useEffect(() => {
    if (!data?.patient) return;
    reset({
      salutation: data.patient.salutation ?? "",
      name: (data.patient.name ?? "").toUpperCase(),
      dob: data.patient.dob?.slice(0, 10) ?? "",
      noDob: false,
      ageYears: undefined,
      ageMonths: undefined,
      gender: (data.patient.gender ?? undefined) as any,
      phone: data.patient.phone ?? "",
      altPhone: data.patient.altPhone ?? "",
      email: data.patient.email ?? "",
      address: data.patient.address ?? "",
      state: data.patient.state ?? "TN",
      pincode: data.patient.pincode ?? "",
      aadhaarId: data.patient.aadhaarId ?? "",
      abhaId: data.patient.abhaId ?? "",
    });
  }, [data?.patient, reset]);

  // Selected flag IDs — local state, seeded from the patient's existing flags on edit.
  // Use a Set for O(1) toggle. Initialized once when patientFlagsData arrives.
  const [selectedFlagIds, setSelectedFlagIds] = useState<string[]>(initialFlagIds);
  useEffect(() => {
    setSelectedFlagIds(initialFlagIds);
  }, [initialFlagIds.join(",")]);

  // New-patient flow: photo captured before creation is staged as a blob
  // (preview-only) and uploaded via POST /patients/:id/photo right after the
  // patient row exists. Edit flow: the photo card uploads directly.
  const [stagedPhoto, setStagedPhoto] = useState<Blob | null>(null);
  const [stagedPhotoUrl, setStagedPhotoUrl] = useState<string | null>(null);
  const onPhotoStaged = (blob: Blob) => {
    setStagedPhoto(blob);
    setStagedPhotoUrl((u) => { if (u) URL.revokeObjectURL(u); return URL.createObjectURL(blob); });
  };
  useEffect(() => () => { if (stagedPhotoUrl) URL.revokeObjectURL(stagedPhotoUrl); }, [stagedPhotoUrl]);

  const toggleFlag = (flagId: string) => {
    setSelectedFlagIds((prev) => prev.includes(flagId) ? prev.filter((id) => id !== flagId) : [...prev, flagId]);
  };

  // Duplicate-patient guard state (2026-10-28): matches found by the pre-check
  // + the pending body awaiting explicit confirmation. The dialog lists the
  // existing records beside the new entry; "Add as new patient anyway"
  // resumes the create, "Go back and edit" aborts it.
  const [dupMatches, setDupMatches] = useState<DuplicatePatientMatch[] | null>(null);
  const [pendingBody, setPendingBody] = useState<Record<string, unknown> | null>(null);

  const onSubmit = async (v: FormValues) => {
    const { noDob, ...rest } = v;
    // Enforce FULL CAPS for the patient name regardless of how it was entered.
    if (rest.name) rest.name = rest.name.toUpperCase();
    const body: Record<string, unknown> = { ...rest, updatedAt: data?.patient?.updatedAt };
    if (noDob) {
      delete body.dob;
      body.ageYears = v.ageYears;
      // Months are optional: default to 0 when only years are entered.
      body.ageMonths = v.ageMonths ?? 0;
    } else {
      delete body.ageYears;
      delete body.ageMonths;
    }
    // Always send flagIds (create attaches them; PATCH replaces the set).
    body.flagIds = selectedFlagIds;
    if (patientId) {
      await doUpdate(body as any);
    } else {
      await startCreate(body as any);
    }
  };

  // Edit flow -- unchanged (the guard is create-only).
  const doUpdate = async (body: Record<string, unknown>) => {
    try {
      await updateMut.mutateAsync(body as any);
      onSaved ? onSaved(patientId!) : navigate(`/patients/${patientId}`);
    } catch { /* handled */ }
  };

  // Create flow: run the advisory duplicate pre-check first. On matches, park
  // the body and show the confirmation dialog; on a clean check (or a failed
  // check -- it is advisory, never a blocker) create immediately.
  const startCreate = async (body: Record<string, unknown>) => {
    const entry = {
      name: String(body.name ?? ""),
      phone: body.phone ? String(body.phone) : undefined,
      altPhone: body.altPhone ? String(body.altPhone) : undefined,
      dob: body.dob ? String(body.dob) : undefined,
      ageYears: typeof body.ageYears === "number" ? (body.ageYears as number) : undefined,
      ageMonths: typeof body.ageMonths === "number" ? (body.ageMonths as number) : undefined,
    };
    // Advisory only -- a failed check never blocks creation (treated as clean).
    const matches = await dupCheckMut
      .mutateAsync(entry)
      .then((r) => (r?.matches ?? []) as DuplicatePatientMatch[])
      .catch(() => [] as DuplicatePatientMatch[]);
    if (matches.length > 0) {
      setDupMatches(matches);
      setPendingBody(body);
    } else {
      await doCreate(body);
    }
  };

  // Confirmed create (either the check was clean, or the user explicitly chose
  // "Add as new patient anyway" in the duplicate dialog).
  const doCreate = async (body: Record<string, unknown>) => {
    setDupMatches(null);
    setPendingBody(null);
    try {
      const r = await createMut.mutateAsync(body as any);
      // Upload the staged photo (if any) right after the patient exists.
      // The API enforces the JPEG/dims/DPI contract, so a failed staged
      // upload only fails the photo — the patient record is already in
      // (a toast surfaces the photo error; navigation continues).
      if (stagedPhoto) {
        try {
          await photoUploadMut.mutateAsync({ patientId: r.patient.id, blob: stagedPhoto });
        } catch (photoErr) {
          const e = photoErr as Error & { body?: { message?: string } };
          toast.error(e?.body?.message || "Patient created, but the photo failed to save");
        }
      }
      onSaved ? onSaved(r.patient.id) : navigate(`/patients/${r.patient.id}`);
    } catch { /* handled */ }
  };

  if (patientId && isLoading) return <Skeleton className="h-96" />;

  const noDob = watch("noDob");

  return (
    <Card className="mx-auto max-w-4xl">
      <CardHeader><CardTitle className="text-maroon-700 dark:text-amber-500">{patientId ? "Edit patient" : "New patient"}</CardTitle></CardHeader>
      <CardContent>
        <div className="flex flex-col-reverse items-start gap-6 md:flex-row">
          {/* Photo column — left on wide screens (per spec), stacked below on mobile. */}
          <div className="w-full shrink-0 md:w-44">
            {patientId ? (
              <PatientPhotoCard patientId={patientId} photoR2Key={photoR2Key} />
            ) : stagedPhotoUrl ? (
              <StagedPhotoPreview url={stagedPhotoUrl} onRetake={() => {
                setStagedPhoto(null);
                setStagedPhotoUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
              }} />
            ) : (
              <PatientPhotoCard onPhotoStaged={onPhotoStaged} />
            )}
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="min-w-0 flex-1 space-y-4">
          {patientId && data?.patient?.clinicUhid && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>UHID (read-only)</Label>
                <Input value={data.patient.clinicUhid} readOnly className="font-mono bg-cream-50 dark:bg-dark-700" />
              </div>
              <div>
                <Label>Old UHID {data.patient.oldUhid ? "(read-only)" : "(not set)"}</Label>
                <Input value={data.patient.oldUhid ?? ""} readOnly placeholder="—" className="font-mono bg-cream-50 dark:bg-dark-700" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Salutation *</Label>
              <Select value={watch("salutation")} onValueChange={ignoreSpuriousEmpty((v) => setValue("salutation", v, { shouldValidate: true }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{SALUTATIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {errors.salutation && <p className="text-xs text-red-600">{errors.salutation.message}</p>}
            </div>
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input
                {...register("name", {
                  onChange: (e) => setValue("name", e.target.value.toUpperCase(), { shouldValidate: true }),
                })}
                style={{ textTransform: "uppercase" }}
              />
              {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="flex items-center gap-1.5 text-xs text-muted mb-1">
                <input type="checkbox" {...register("noDob")} className="h-3.5 w-3.5" />
                No date of birth available
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Date of birth {!noDob && "*"}</Label>
                  <Input type="date" disabled={noDob} {...register("dob")} />
                </div>
                <div>
                  <Label>Age (years / months) {noDob && "*"}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" min={0} max={130} placeholder="Yrs" disabled={!noDob} {...register("ageYears", { setValueAs: (v) => v === "" || v == null ? undefined : Number.isNaN(Number(v)) ? undefined : Number(v) })} />
                    <Input type="number" min={0} max={11} placeholder="Mo (opt)" disabled={!noDob} {...register("ageMonths", { setValueAs: (v) => v === "" || v == null ? undefined : Number.isNaN(Number(v)) ? undefined : Number(v) })} />
                  </div>
                </div>
              </div>
              {errors.dob && <p className="text-xs text-red-600">{errors.dob.message}</p>}
              {errors.ageYears && <p className="text-xs text-red-600">{errors.ageYears.message}</p>}
              {errors.ageMonths && <p className="text-xs text-red-600">{errors.ageMonths.message}</p>}
            </div>
            <div>
              <Label>Gender *</Label>
              <Select value={watch("gender") ?? ""} onValueChange={ignoreSpuriousEmpty((v) => setValue("gender", v as any, { shouldValidate: true }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{GENDERS.map((g: any) => <SelectItem key={g.value ?? g} value={g.value ?? g}>{g.label ?? g}</SelectItem>)}</SelectContent>
              </Select>
              {errors.gender && <p className="text-xs text-red-600">{errors.gender.message}</p>}
            </div>
            <div>
              <Label>Phone *</Label>
              <Input {...register("phone")} placeholder="10-digit mobile or 0-STD landline" />
              {errors.phone && <p className="text-xs text-red-600">{errors.phone.message}</p>}
            </div>
            <div><Label>Alt phone</Label><Input {...register("altPhone")} placeholder="10-digit mobile or 0-STD landline" /></div>
            <div>
              <Label>Email</Label>
              <Input type="email" {...register("email")} />
              {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div className="col-span-2">
              <Label>Address *</Label>
              <Input {...register("address")} />
              {errors.address && <p className="text-xs text-red-600">{errors.address.message}</p>}
            </div>
            <div>
              <Label>State *</Label>
              <Select value={watch("state") ?? (patientId ? "" : "TN")} onValueChange={ignoreSpuriousEmpty((v) => setValue("state", v, { shouldValidate: true }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{INDIAN_STATES.map((s: any) => <SelectItem key={s.code ?? s} value={s.code ?? s}>{s.name ?? s}</SelectItem>)}</SelectContent>
              </Select>
              {errors.state && <p className="text-xs text-red-600">{errors.state.message}</p>}
            </div>
            <div>
              <Label>Pincode</Label>
              <Input {...register("pincode")} maxLength={6} />
              {errors.pincode && <p className="text-xs text-red-600">{errors.pincode.message}</p>}
            </div>
            <div>
              <Label>Aadhaar ID</Label>
              <Input {...register("aadhaarId")} placeholder="12-digit Aadhaar or 16-digit VID" />
              {errors.aadhaarId && <p className="text-xs text-red-600">{errors.aadhaarId.message}</p>}
            </div>
            <div><Label>ABHA ID</Label><Input {...register("abhaId")} /></div>
          </div>

          <div>
            <Label className="block mb-2">Flags</Label>
            {catalog.length === 0 ? (
              <p className="text-xs text-muted">No flags configured. Add flags via Settings → Flags.</p>
            ) : (
              <div className="space-y-2">
                {/* Dropdown to add a flag — only unselected flags are listed. */}
                <Select onValueChange={(fid) => { if (!selectedFlagIds.includes(fid)) toggleFlag(fid); }}>
                  <SelectTrigger className="w-56"><SelectValue placeholder="Add flag…" /></SelectTrigger>
                  <SelectContent>
                    {catalog.filter((f: any) => !selectedFlagIds.includes(f.id)).map((f: any) => (
                      <SelectItem key={f.id} value={f.id}>
                        <span className="flex items-center gap-2">
                          <span className={cn("inline-block h-3 w-3 rounded-full border border-black/10", swatchBg(f.color || "bg-red-100 text-red-800 border-red-300"))} />
                          {f.name}
                          {f.kind === "admin" && <span className="text-[10px] text-purple-700">(admin)</span>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Selected flags — shown as coloured badges with a cross button to remove. */}
                {selectedFlagIds.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedFlagIds.map((fid) => {
                      const f = (catalog as any[]).find((c) => c.id === fid);
                      if (!f) return null;
                      return (
                        <Badge key={fid} variant={null} className={cn("flex items-center gap-1 border", f.color || "bg-red-100 text-red-800 border-red-300")}>
                          {f.name}
                          <button type="button" onClick={() => toggleFlag(fid)} className="ml-1 rounded-full hover:bg-black/10"><X size={12} /></button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
            <Button type="submit" disabled={createMut.isPending || updateMut.isPending || dupCheckMut.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">{patientId ? "Save changes" : "Create patient"}</Button>
          </div>
        </form>
        </div>

        {/* Duplicate-patient guard (2026-10-28): matches from the advisory
            pre-check; "Add as new patient anyway" resumes the parked create. */}
        <DuplicatePatientDialog
          open={dupMatches !== null}
          onOpenChange={(v) => {
            if (!v) { setDupMatches(null); setPendingBody(null); }
          }}
          matches={dupMatches ?? []}
          entry={pendingBody ? {
            name: String(pendingBody.name ?? ""),
            phone: pendingBody.phone ? String(pendingBody.phone) : "",
            altPhone: pendingBody.altPhone ? String(pendingBody.altPhone) : undefined,
            dob: pendingBody.dob ? String(pendingBody.dob) : undefined,
            ageYears: typeof pendingBody.ageYears === "number" ? (pendingBody.ageYears as number) : undefined,
            ageMonths: typeof pendingBody.ageMonths === "number" ? (pendingBody.ageMonths as number) : undefined,
          } : { name: "", phone: "" }}
          onConfirmAddAnyway={() => { if (pendingBody) void doCreate(pendingBody); }}
        />
      </CardContent>
    </Card>
  );
}

/** In-memory preview of a staged (not yet uploaded) photo — new-patient flow. */
function StagedPhotoPreview({ url, onRetake }: { url: string; onRetake: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <img src={url} alt="Staged patient photo"
        className="h-[159px] w-[124px] rounded-md border border-cream-200 object-cover dark:border-dark-700" />
      <Button type="button" size="sm" variant="outline" onClick={onRetake}>
        <X size={14} className="mr-1" /> Retake
      </Button>
    </div>
  );
}
